import { useEffect, useState, useMemo, useRef } from 'react'
import {
  Form,
  Input,
  Select,
  Checkbox,
  Radio,
  Button,
  Tooltip,
  Switch,
  InputNumber,
  Space,
  DatePicker,
  Cascader,
  TimePicker,
  TreeSelect,
  Badge,
} from 'antd'
import { SvgIcon, BasSwitch } from '@/components'
import styled from 'styled-components'
import moment from 'moment'
import { useTaskGroup, useGetAgent, useGetChainInfo, useGetEmail } from './utils'
import { BaseInfo } from '@/api/threat-list-task'
import { useGetTaskBaseInfo } from '../task-list/utils'
import { disabledDate, disabledDateTime } from '@/utils/appointment'
import type { DefaultOptionType } from 'antd/es/cascader'
import { useNavigate, useLocation } from 'react-router-dom'
import React from 'react'
import { t } from '@/i18n'

interface SecurityDevice {
  deviceId: string
  deviceName: string
  deviceIp: string
  deviceStatus: string
  chainId: string
}

interface SecurityDeviceType {
  name: string
  chainId: string
  typeDeviceList: {
    deviceType: string
    devices: SecurityDevice[]
  }[]
}

interface WAFCreateConfigProps {
  submitForm: (values: any) => void
  createForm: any
  initialValues?: any
  areaData: any
  templateState?: {
    selectedTemplateIds: string[]
    selectedCaseIds: string[]
    executeTemplateLatestCase: boolean
    selectedTemplateId?: string
    templateSwitchStates: Record<string, boolean>
  }
  onTemplateStateChange?: (newState: {
    selectedTemplateIds?: string[]
    selectedCaseIds?: string[]
    executeTemplateLatestCase?: boolean
    selectedTemplateId?: string
    templateSwitchStates: Record<string, boolean>
  }) => void
}

export const TpCreateConfig = ({
  submitForm,
  createForm,
  initialValues,
  areaData,
  templateState,
  onTemplateStateChange,
}: WAFCreateConfigProps) => {
  const navigate = useNavigate()
  const { state: stateParams } = useLocation() as { state: { id?: string } }
  const [taskGroup, getTaskGroup] = useTaskGroup()
  const [agentList, getAgentList] = useGetAgent()
  const [emailList] = useGetEmail()
  const [securityDevice, getSecurityDevice] = useGetChainInfo()
  const [getTaskBaseInfo, taskBaseInfo] = useGetTaskBaseInfo()
  const [securityDeviceData, setSecurityDeviceData] = useState<SecurityDeviceType[]>([])
  const [selectedChainId, setSelectedChainId] = useState<string | null>(null)
  const [monthList, setMonthList] = useState<{ title: string; value: number }[]>([])
  const [apiCallCount, setApiCallCount] = useState(0) // 添加接口调用计数器
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[][]>([]) // 跟踪选择的设备
  const [isInitialLoad, setIsInitialLoad] = useState(false) // 标记是否已经初始化加载过

  // 根据当前选中的第一个设备的链路ID动态决定是否允许多选
  const deviceMultiple = useMemo(() => {
    // 如果没有选择任何设备，默认允许多选
    if (selectedDeviceIds.length === 0) {
      return true
    }

    // 获取第一个选中设备的链路ID
    const firstDevicePath = selectedDeviceIds[0]
    if (Array.isArray(firstDevicePath) && firstDevicePath.length > 0) {
      const firstChainId = firstDevicePath[0]
      // 如果第一个链路ID为"0"（无链路设备），则单选；否则多选
      return firstChainId !== '0'
    }

    // 默认情况下允许多选
    return true
  }, [selectedDeviceIds])

  // 判断是否选择了无链路设备（用于显示提示信息）
  const isNoChainDeviceSelected = useMemo(() => {
    if (selectedDeviceIds.length === 0) {
      return false
    }

    const firstDevicePath = selectedDeviceIds[0]
    return Array.isArray(firstDevicePath) && firstDevicePath[0] === '0'
  }, [selectedDeviceIds])

  // 合并状态管理
  const [formState, setFormState] = useState({
    verifyMode: initialValues?.verification_mode_id || [2],
    executeType: initialValues?.execute_type || 1,
    executeTimes: initialValues?.execute_times || 1,
    taskTime: initialValues?.task_time
      ? moment.isMoment(initialValues.task_time)
        ? initialValues.task_time
        : moment(initialValues.task_time, 'HH:mm:ss')
      : undefined,
    send: initialValues?.send || false,
    filter: initialValues?.filter || false,
    emailList: initialValues?.emailList || [],
    condition: initialValues?.condition || 'condition_anyway',
    schedulerType: initialValues?.scheduler_type || 'scheduler_type_daily',
    executeTimesLimit: initialValues?.execution_times || 0,
    dayOfWeek: initialValues?.day_of_week || [],
    dayOfMonth: initialValues?.day_of_month || [],
    executeTemplateLatestCase: initialValues?.execute_template_latest_case ?? true,
    webAppCount: 0,
    reduceSpeed: initialValues?.reduce_speed || 20,
    priority: initialValues?.priority || 3,
    pcapCollection: initialValues?.pcap_collection || false,
    isNat: initialValues?.is_nat || false,
  })

  // 使用 useRef 来跟踪是否已经初始化
  const isInitialized = useRef(false)
  const taskTimeRef = useRef<moment.Moment | undefined>(formState.taskTime)
  const taskTimeMomentRef = useRef<moment.Moment | undefined>(formState.taskTime)
  const initialTaskTimeRef = useRef<moment.Moment | undefined>(formState.taskTime)
  const isTimeInitialized = useRef(false)
  const isTimeUpdating = useRef(false)

  // 初始化 taskTimeMomentRef
  useEffect(() => {
    if (initialValues?.task_time && !isTimeInitialized.current) {
      let momentTime: moment.Moment | undefined
      if (moment.isMoment(initialValues.task_time)) {
        momentTime = initialValues.task_time
      } else if (typeof initialValues.task_time === 'string') {
        momentTime = moment(initialValues.task_time, 'HH:mm:ss')
      }
      if (momentTime && momentTime.isValid()) {
        taskTimeMomentRef.current = momentTime
        taskTimeRef.current = momentTime
        initialTaskTimeRef.current = momentTime
        isTimeInitialized.current = true
        setFormState(prev => ({ ...prev, taskTime: momentTime }))
        console.log('Initialized time from initialValues:', momentTime.format('HH:mm:ss'))
      }
    }
  }, [initialValues?.task_time])

  // 更新 taskTimeRef 当 taskTime 变化时
  useEffect(() => {
    if (
      formState.taskTime &&
      (!taskTimeRef.current || !formState.taskTime.isSame(taskTimeRef.current)) &&
      !isTimeUpdating.current
    ) {
      if (formState.taskTime.isValid()) {
        taskTimeRef.current = formState.taskTime
        taskTimeMomentRef.current = formState.taskTime
        console.log('Updated time from formState:', formState.taskTime.format('HH:mm:ss'))
      }
    }
  }, [formState.taskTime])

  console.log('stateParams', stateParams)

  // useEffect(() => {
  //   if (stateParams?.id && !isInitialized.current) {
  //     isInitialized.current = true
  //     getTaskBaseInfo(stateParams.id)
  //       .then((res: BaseInfo) => {
  //         console.log('res9999999999999999', res)
  //         const taskKind = res.data.taskKind
  //         let type
  //         if (taskKind === 'TaskKindDetailImmediately') {
  //           type = 1
  //         } else if (taskKind === 'TaskKindDetailBooking') {
  //           type = 2
  //         } else if (
  //           taskKind === 'TaskKindDetailCycleDay' ||
  //           taskKind === 'TaskKindDetailCycleWeek' ||
  //           taskKind === 'TaskKindDetailCycleMonth'
  //         ) {
  //           type = 3
  //         }

  //         // 处理时间格式
  //         let taskTimeMoment: moment.Moment | undefined
  //         if (type === 3 && res.data.cronSchedule?.timeOfDay) {
  //           const timeParts = res.data.cronSchedule.timeOfDay.split(' ')
  //           const timeString = timeParts[timeParts.length - 1] || ''
  //           if (timeString && timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
  //             taskTimeMoment = moment(timeString, 'HH:mm:ss')
  //           }
  //         } else if ((type === 2 || type === 4) && res.data.cycleTime) {
  //           taskTimeMoment = moment(res.data.cycleTime, 'HH:mm:ss')
  //         }

  //         console.log('Final taskTime:', taskTimeMoment)

  //         if (taskTimeMoment && taskTimeMoment.isValid()) {
  //           isTimeUpdating.current = true
  //           taskTimeMomentRef.current = taskTimeMoment
  //           taskTimeRef.current = taskTimeMoment
  //           initialTaskTimeRef.current = taskTimeMoment
  //           isTimeInitialized.current = true
  //           console.log('Valid moment time:', taskTimeMoment.format('HH:mm:ss'))
  //           isTimeUpdating.current = false
  //           setFormState(prev => ({
  //             ...prev,
  //             executeType: type,
  //             schedulerType: taskKind,
  //             taskTime: taskTimeMoment,
  //             executeTimesLimit: res.data.executeTimesLimit || 0,
  //             dayOfWeek: res.data.cronSchedule?.dayOfWeek || [],
  //             dayOfMonth: res.data.cronSchedule?.dayOfMonth || [],
  //             isNat: res.data.isNat || false,
  //             pcapCollection: res.data.pcapCollection || false,
  //             priority: res.data.priority || 3,
  //           }))

  //           // 设置表单值
  //           const formValues = {
  //             name: res.data.name,
  //             business_site_id: res.data.siteId,
  //             priority: res.data.priority,
  //             send: res.data?.reportConfig?.send,
  //             filter: res.data?.reportConfig?.filter,
  //             emailList: res.data?.reportConfig?.emailList,
  //             condition: res.data?.reportConfig?.condition || 'condition_anyway',
  //             proxy_pool_id: res.data.poolId === '0' ? undefined : res.data.poolId,
  //             group_id: res.data.groupId,
  //             execute_type: type,
  //             scheduler_type: taskKind,
  //             task_time: taskTimeMoment,
  //             execution_times: res.data.executeTimesLimit || 0,
  //             day_of_week: res.data.cronSchedule?.dayOfWeek || [],
  //             day_of_month: res.data.cronSchedule?.dayOfMonth || [],
  //             is_nat: res.data?.agentToAgent?.isNat || false,
  //             sNat: res.data?.agentToAgent?.sNat || '',
  //             middleIps: res.data?.agentToAgent?.middleIps || '',
  //             dstAgentId: res.data?.agentToAgent?.dstAgentId || [],
  //             srcAgentId: res.data?.agentToAgent?.srcAgentId || [],
  //           }

  //           console.log('Setting form values with task_time:', taskTimeMoment?.format('HH:mm:ss'))

  //           // 确保所有值都设置后再调用 setFieldsValue
  //           createForm.setFieldsValue(formValues)
  //         }
  //       })
  //       .catch(_ => {})
  //   }
  // }, [stateParams?.id])

  // 监听 initialValues 变化
  useEffect(() => {
    if (initialValues && !isInitialized.current) {
      console.log('initialValues-----------3333', initialValues)

      let taskTimeMoment: moment.Moment | undefined
      if (initialValues.task_time) {
        if (typeof initialValues.task_time === 'string') {
          taskTimeMoment = moment(initialValues.task_time, 'HH:mm:ss')
        } else if (moment.isMoment(initialValues.task_time)) {
          taskTimeMoment = initialValues.task_time
        }
      }

      // 如果已经有初始时间值，则使用它
      if (initialTaskTimeRef.current) {
        taskTimeMoment = initialTaskTimeRef.current
      }

      console.log('taskTime in initialValues', taskTimeMoment)

      // 更新 formState
      setFormState(prev => ({
        ...prev,
        ...initialValues,
        verifyMode: initialValues.verification_mode_id || prev.verifyMode,
        executeType: initialValues.execute_type || prev.executeType,
        executeTimes: initialValues.execute_times || prev.executeTimes,
        taskTime: taskTimeMoment || prev.taskTime,
        send: initialValues.send,
        filter: initialValues.filter,
        emailList: initialValues.emailList,
        condition: initialValues.condition || prev.condition,
        schedulerType: initialValues.scheduler_type || prev.schedulerType,
        executeTimesLimit: initialValues.execution_times || prev.executeTimesLimit,
        dayOfWeek: initialValues.day_of_week || prev.dayOfWeek,
        dayOfMonth: initialValues.day_of_month || prev.dayOfMonth,
        executeTemplateLatestCase:
          initialValues.execute_template_latest_case ?? prev.executeTemplateLatestCase,
        reduceSpeed: initialValues.reduce_speed || prev.reduceSpeed,
        priority: initialValues.priority || prev.priority || 3,
        pcapCollection: initialValues.pcap_collection ?? prev.pcapCollection,
        isNat: initialValues.is_nat ?? prev.isNat,
      }))

      // 设置表单值
      const formValues = {
        ...initialValues,
        task_time: taskTimeMoment,
        execution_times: initialValues.execution_times || 0,
        day_of_week: initialValues.day_of_week || [],
        day_of_month: initialValues.day_of_month || [],
        is_nat: initialValues.is_nat || false,
        sNat: initialValues.sNat || '',
        middleIps: initialValues.middleIps || '',
        dstAgentId: initialValues.dstAgentId || [],
        srcAgentId: initialValues.srcAgentId || [],
        priority: initialValues.priority,
      }

      // 确保所有值都设置后再调用 setFieldsValue
      console.log('-------formValues------', formValues)
      createForm.setFieldsValue(formValues)
    }
  }, [initialValues])

  // 初始化数据
  useEffect(() => {
    // 初始化月份列表
    const arr = Array.from({ length: 31 }, (_, i) => ({
      title: `${i + 1}日`,
      value: i + 1,
    }))
    setMonthList(arr)

    console.log('initialValues', initialValues)
    console.log('formState22222222222222', formState)

    // 并行获取数据
    Promise.all([getAgentList(), getTaskGroup({ page: 1, limit: 10000 })])
      .then(([__, res]) => {
        const initialFormValues = {
          group_id:
            initialValues?.group_id ||
            res.data.groups.find(item => item.groupName === '常规任务')?.groupId,
          priority: formState.priority,
          execute_template_latest_case: formState.executeTemplateLatestCase,
          verification_mode_id: formState.verifyMode,
          name: initialValues?.name || '',
          execute_type: formState.executeType,
          execute_times: formState.executeTimes,
          execute_interval: initialValues?.execute_interval || 1,
          execute_unit: initialValues?.execute_unit || 'hour',
          execution_times: formState.executeTimesLimit,
          task_time: formState.taskTime,
          reduce_speed: formState.reduceSpeed,
          send: initialValues?.send,
          filter: initialValues?.filter,
          emailList: initialValues?.emailList,
          condition: initialValues?.condition || 'condition_anyway',
          dstAgentId: initialValues?.dstAgentId || [],
          srcAgentId: initialValues?.srcAgentId || [],
          device_ids: initialValues?.device_ids || [],
          scheduler_type: formState.schedulerType,
          day_of_week: formState.dayOfWeek,
          day_of_month: formState.dayOfMonth,
          is_nat: formState.isNat,
          sNat: initialValues?.sNat || '',
          node_ip: initialValues?.node_ip || '',
        }

        console.log('-------initialFormValues------', initialFormValues)

        createForm.setFieldsValue(initialFormValues)
      })
      .catch(_ => {})
  }, [])

  // 获取攻击主体和目标主体的areaId
  const getAreaIds = () => {
    const attackAgentIds = createForm.getFieldValue('srcAgentId') || []
    const targetAgentIds = createForm.getFieldValue('dstAgentId') || []

    let srcAreaId: string | null = null
    let dstAreaId: string | null = null

    // 获取攻击主体的areaId
    if (
      attackAgentIds.length > 0 &&
      Array.isArray(attackAgentIds[0]) &&
      attackAgentIds[0].length >= 2
    ) {
      const attackDomainId = attackAgentIds[0][0]
      const attackAgentId = attackAgentIds[0][1]

      // 在agentMockList中查找对应的安全域
      const attackDomain = agentList.find(d => d.id === attackDomainId)
      if (attackDomain) {
        srcAreaId = attackDomain.id
      }
    }

    // 获取目标主体的areaId
    if (
      targetAgentIds.length > 0 &&
      Array.isArray(targetAgentIds[0]) &&
      targetAgentIds[0].length >= 2
    ) {
      const targetDomainId = targetAgentIds[0][0]
      const targetAgentId = targetAgentIds[0][1]

      // 在agentMockList中查找对应的安全域
      const targetDomain = agentList.find(d => d.id === targetDomainId)
      if (targetDomain) {
        dstAreaId = targetDomain.id
      }
    }

    return { srcAreaId, dstAreaId }
  }

  // 监听Agent选择变化
  useEffect(() => {
    const agentIds = createForm.getFieldValue('dstAgentId') || []
    setFormState(prev => ({ ...prev, webAppCount: agentIds.length }))

    if (agentIds.length === 1 && Array.isArray(agentIds[0])) {
      // 这里可以根据选择的agent来获取相关的安全设备信息
      // 暂时保持原有的逻辑，后续可以根据实际需求调整
      const selectedAgentId = agentIds[0][1] // 获取选中的agent ID
      const selectedIP = agentIds[0][2] // 获取选中的IP（如果有的话）
      console.log('Selected agent ID:', selectedAgentId, 'Selected IP:', selectedIP)
    }
  }, [createForm.getFieldValue('dstAgentId')])

  // 监听攻击主体和目标主体变化，调用getSecurityDevice接口
  useEffect(() => {
    const attackAgentIds = createForm.getFieldValue('srcAgentId') || []
    const targetAgentIds = createForm.getFieldValue('dstAgentId') || []

    // 只有在非初始化状态且攻击主体和目标主体都选择后，才调用接口
    if (attackAgentIds.length > 0 && targetAgentIds.length > 0) {
      const { srcAreaId, dstAreaId } = getAreaIds()
      if (srcAreaId && dstAreaId) {
        // 增加接口调用计数
        setApiCallCount(prev => {
          const newCount = prev + 1
          console.log(`Security device API call count: ${newCount}`)

          // 调用接口
          getSecurityDevice({ srcAreaId, dstAreaId })
            .then(res => {
              console.log('用户选择变化，安全设备数据加载完成:', res.data.chainDeviceList)
              setSecurityDeviceData(res.data.chainDeviceList)
            })
            .catch(_ => {})

          return newCount
        })
      }
    }
  }, [createForm.getFieldValue('srcAgentId'), createForm.getFieldValue('dstAgentId')])

  // 监听NAT模式变化，同步更新表单字段
  useEffect(() => {
    createForm.setFieldsValue({ is_nat: formState.isNat })
  }, [formState.isNat, createForm])

  // 初始化时主动获取安全设备数据
  useEffect(() => {
    const srcAgentId = initialValues?.srcAgentId
    const dstAgentId = initialValues?.dstAgentId

    // 只在初始化时且还没有加载过的情况下执行
    if (
      srcAgentId?.length > 0 &&
      dstAgentId?.length > 0 &&
      agentList.length > 0 &&
      !isInitialLoad
    ) {
      console.log('初始化时主动获取安全设备数据')
      let srcAreaId: string | null = null
      let dstAreaId: string | null = null

      // 获取攻击主体的areaId
      if (Array.isArray(srcAgentId[0]) && srcAgentId[0].length >= 2) {
        const attackDomainId = srcAgentId[0][0]
        const attackDomain = agentList.find(d => d.id === attackDomainId)
        if (attackDomain) {
          srcAreaId = attackDomain.id
        }
      }

      // 获取目标主体的areaId
      if (Array.isArray(dstAgentId[0]) && dstAgentId[0].length >= 2) {
        const targetDomainId = dstAgentId[0][0]
        const targetDomain = agentList.find(d => d.id === targetDomainId)
        if (targetDomain) {
          dstAreaId = targetDomain.id
        }
      }

      if (srcAreaId && dstAreaId) {
        setIsInitialLoad(true) // 标记已经开始初始化加载
        getSecurityDevice({ srcAreaId, dstAreaId })
          .then(res => {
            console.log('初始化获取安全设备数据完成:', res.data.chainDeviceList)
            setSecurityDeviceData(res.data.chainDeviceList)
          })
          .catch(_ => {})
      }
    }
  }, [initialValues?.srcAgentId, initialValues?.dstAgentId, agentList, isInitialLoad])

  // 在安全设备数据加载完成后恢复设备选择状态
  useEffect(() => {
    const deviceIds = initialValues?.device_ids || []
    console.log('恢复设备选择状态:', { deviceIds, securityDeviceData })

    // 只有当安全设备数据存在且有待恢复的设备ID时才执行恢复
    if (deviceIds.length > 0 && securityDeviceData.length > 0) {
      console.log('开始恢复设备选择状态')
      setSelectedDeviceIds(deviceIds)
      // 确保表单字段也被设置
      createForm.setFieldsValue({ device_ids: deviceIds })

      // 恢复选中的链路ID（现在第一级就是chainId）
      if (Array.isArray(deviceIds[0]) && deviceIds[0].length >= 3) {
        const chainId = deviceIds[0][0] // 第一级就是chainId
        console.log('恢复链路ID:', chainId)
        setSelectedChainId(chainId)
      }
    }
  }, [securityDeviceData, initialValues?.device_ids, createForm])

  // 处理无链路设备的单选限制
  // useEffect(() => {
  //   if (selectedDeviceIds.length > 1 && isNoChainDevice) {
  //     // 如果是无链路设备且选择了多个，只保留最后一个
  //     const lastSelected = selectedDeviceIds[selectedDeviceIds.length - 1]
  //     const newValue = [lastSelected]
  //     setSelectedDeviceIds(newValue)
  //     createForm.setFieldsValue({ device_ids: newValue })
  //   }
  // }, [selectedDeviceIds, isNoChainDevice, createForm])

  // 获取已选择的攻击主体IP列表
  const getSelectedAttackIPs = () => {
    const attackAgentIds = createForm.getFieldValue('srcAgentId') || []
    if (attackAgentIds.length === 0) return []

    const selectedIPs: string[] = []
    if (!Array.isArray(attackAgentIds)) return []
    attackAgentIds.forEach((agentPath: string[]) => {
      if (Array.isArray(agentPath)) {
        const domainId = agentPath[0]
        const agentId = agentPath[1]
        const selectedIP = agentPath[2] // 第三级IP值

        // 在agentMockList中查找对应的agent
        const domain = agentList.find(d => d.id === domainId)
        if (domain) {
          const agent = domain.os.flatMap(os => os.agents).find(a => a.agentId === agentId)
          if (agent) {
            // 如果选择了具体的IP，只添加该IP；否则添加所有IP
            if (selectedIP && agent.ip.includes(selectedIP)) {
              selectedIPs.push(selectedIP)
            } else {
              selectedIPs.push(...agent.ip)
            }
          }
        }
      }
    })
    return selectedIPs
  }

  // 根据攻击主体选择过滤目标主体选项
  const getFilteredTargetAgentOptions = () => {
    const selectedAttackIPs = getSelectedAttackIPs()

    return agentList
      .map(domain => {
        // 过滤出有IP的agents
        const agentsWithIP = domain.os
          .flatMap(os => os.agents)
          .filter(agent => agent.ip && agent.ip.length > 0)

        // 统计有IP的agent总数
        const totalAgentsWithIP = agentsWithIP.length

        // 如果没有有IP的agent，则不显示该安全域
        if (totalAgentsWithIP === 0) {
          return null
        }

        return {
          value: domain.id,
          label: (
            <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>{domain.name}</span>
              {totalAgentsWithIP > 0 && (
                <span style={{ color: '#4238C9', marginLeft: '8px' }}>({totalAgentsWithIP})</span>
              )}
            </span>
          ),

          children: agentsWithIP
            .sort((a, b) => {
              // 在线的排在前面
              if (a.aliveStatus === 'AliveStatusTrue' && b.aliveStatus !== 'AliveStatusTrue') {
                return -1
              }
              if (a.aliveStatus !== 'AliveStatusTrue' && b.aliveStatus === 'AliveStatusTrue') {
                return 1
              }
              return 0
            })
            .map(agent => {
              // 检查IP是否与攻击主体冲突
              const hasIPConflict = agent.ip.some(ip => selectedAttackIPs.includes(ip))

              return {
                value: agent.agentId,
                disabled: agent.aliveStatus !== 'AliveStatusTrue' || hasIPConflict,
                label: (
                  <span>
                    {agent.agentName}
                    {hasIPConflict && (
                      <span style={{ color: '#ff4d4f', fontSize: '12px', marginLeft: '8px' }}>
                        {t('(IP冲突)')}
                      </span>
                    )}
                  </span>
                ),

                agent: agent,
                // 添加第三级IP选项
                children: agent.ip.map(ip => ({
                  value: ip,
                  label: (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>{ip}</span>
                      <span style={{ flexShrink: 0 }}>
                        <Badge
                          style={{ marginLeft: 10 }}
                          status={agent.aliveStatus === 'AliveStatusTrue' ? 'success' : 'default'}
                        />
                      </span>
                    </span>
                  ),

                  agent: agent,
                  ip: ip,
                  disabled: selectedAttackIPs.includes(ip), // IP级别的冲突检查
                })),
              }
            }),
        }
      })
      .filter(Boolean) // 过滤掉null值
  }

  // 处理表单值变化
  const handleFormValueChange = (changedValues: any) => {
    console.log('Form values changed:', changedValues)

    if (changedValues.device_ids) {
      // 处理设备选择变化
      setSelectedDeviceIds(changedValues.device_ids)
      return
    }

    if (changedValues.task_time) {
      const newTime = changedValues.task_time
      if (newTime && moment.isMoment(newTime) && newTime.isValid()) {
        isTimeUpdating.current = true
        taskTimeRef.current = newTime
        taskTimeMomentRef.current = newTime
        setFormState(prev => ({
          ...prev,
          ...changedValues,
          taskTime: newTime,
        }))
        isTimeUpdating.current = false
      } else {
        if (taskTimeMomentRef.current) {
          createForm.setFieldsValue({
            task_time: taskTimeMomentRef.current,
          })
        }
      }
    } else {
      const currentTime = taskTimeRef.current || formState.taskTime
      setFormState(prev => ({
        ...prev,
        ...changedValues,
        taskTime: currentTime,
      }))
      if (taskTimeMomentRef.current && !changedValues.task_time) {
        createForm.setFieldsValue({
          task_time: taskTimeMomentRef.current,
        })
      }
    }
  }

  // 处理执行类型变化
  const handleExecuteTypeChange = (value: number) => {
    // 如果切换到高频任务，且之前不是高频任务，则清空用例选择
    if (value === 4 && formState.executeType !== 4) {
      onTemplateStateChange?.({
        selectedTemplateIds: templateState?.selectedTemplateIds || [],
        selectedCaseIds: [],
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    }

    // 保存当前时间值
    const currentTime = taskTimeRef.current || formState.taskTime
    const currentMomentTime = taskTimeMomentRef.current

    isTimeUpdating.current = true
    setFormState(prev => ({
      ...prev,
      executeType: value,
      schedulerType: 'scheduler_type_daily',
      taskTime: currentTime,
      executeTimesLimit: 0,
      dayOfWeek: [],
      dayOfMonth: [],
      executeTemplateLatestCase: value === 3 ? true : false,
    }))

    createForm.setFieldsValue({
      execute_type: value,
      task_time: currentMomentTime,
      scheduler_type: 'scheduler_type_daily',
      execution_times: 0,
      day_of_week: [],
      day_of_month: [],
      execute_template_latest_case: value === 3 ? true : false,
    })
    isTimeUpdating.current = false
  }

  // 处理执行模板最新用例变化
  const handleExecuteTemplateLatestCaseChange = (checked: boolean) => {
    setFormState(prev => ({ ...prev, executeTemplateLatestCase: checked }))
    createForm.setFieldsValue({ execute_template_latest_case: checked })
  }

  // 星期参数
  const [weekList] = useState([
    { title: t('周一'), value: 1 },
    { title: t('周二'), value: 2 },
    { title: t('周三'), value: 3 },
    { title: t('周四'), value: 4 },
    { title: t('周五'), value: 5 },
    { title: t('周六'), value: 6 },
    { title: t('周日'), value: 0 },
  ])

  // Agent级联选择数据
  const cascaderAgentListData = useMemo(
    () =>
      agentList
        .map(domain => {
          // 过滤出有IP的agents
          const agentsWithIP = domain.os
            .flatMap(os => os.agents)
            .filter(agent => agent.ip && agent.ip.length > 0)

          // 统计有IP的agent总数
          const totalAgentsWithIP = agentsWithIP.length

          // 如果没有有IP的agent，则不显示该安全域
          if (totalAgentsWithIP === 0) {
            return null
          }

          return {
            value: domain.id,
            label: (
              <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{domain.name}</span>
                {totalAgentsWithIP > 0 && (
                  <span style={{ color: '#4238C9', marginLeft: '8px' }}>({totalAgentsWithIP})</span>
                )}
              </span>
            ),

            children: agentsWithIP
              .sort((a, b) => {
                // 在线的排在前面
                if (a.aliveStatus === 'AliveStatusTrue' && b.aliveStatus !== 'AliveStatusTrue') {
                  return -1
                }
                if (a.aliveStatus !== 'AliveStatusTrue' && b.aliveStatus === 'AliveStatusTrue') {
                  return 1
                }
                return 0
              })
              .map(agent => ({
                value: agent.agentId,
                disabled: agent.aliveStatus !== 'AliveStatusTrue',
                label: agent.agentName,
                agent: agent,
                // 添加第三级IP选项
                children: agent.ip.map(ip => ({
                  value: ip,
                  label: (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <span>{ip}</span>
                      <span style={{ flexShrink: 0 }}>
                        <Badge
                          style={{ marginLeft: 10 }}
                          status={agent.aliveStatus === 'AliveStatusTrue' ? 'success' : 'default'}
                        />
                      </span>
                    </span>
                  ),

                  agent: agent,
                  ip: ip,
                })),
              })),
          }
        })
        .filter(Boolean), // 过滤掉null值
    [agentList],
  )

  // 递归提取所有 label 文本
  function extractLabelText(label: any): string {
    let result = ''
    if (typeof label === 'string') {
      result = label
    } else if (React.isValidElement(label) && label.props) {
      const children = (label.props as { children?: any }).children
      if (typeof children === 'string') {
        result = children
      } else if (Array.isArray(children)) {
        result = children.map(extractLabelText).join('')
      } else if (children) {
        result = extractLabelText(children)
      }
    }

    // 只移除空的括号 ()，保留包含内容的括号
    // 例如：移除 "默认安全域()" 中的 "()"，但保留 "(localhost.localdomain)" 中的内容
    return result.replace(/\(\)/g, '').trim()
  }

  // Agent选择过滤
  const agentFilter = (inputValue: string, path: DefaultOptionType[]) => {
    const searchValue = inputValue.toLowerCase()
    return path.some(option => {
      const labelText = extractLabelText(option.label).toLowerCase()
      return labelText.includes(searchValue)
    })
  }

  // 关联安全设备过滤
  const deviceFilter = (inputValue: string, path: DefaultOptionType[]) => {
    const searchValue = inputValue.toLowerCase()
    return path.some(option => {
      const labelText = extractLabelText(option.label).toLowerCase()
      return labelText.includes(searchValue)
    })
  }

  // 自定义Agent显示渲染函数
  const agentDisplayRender = (labels: string[], selectedOptions?: DefaultOptionType[]) => {
    if (!selectedOptions || selectedOptions.length === 0) return ''

    // 只取第一个选择项
    const firstOption = selectedOptions[0]

    if (firstOption?.agent) {
      // 获取安全域名称
      const domainName =
        agentList.find(domain =>
          domain.os.some(os =>
            os.agents.some(agent => agent.agentId === firstOption.agent.agentId),
          ),
        )?.name || ''

      const agentName = firstOption.agent.agentName
      const ip = firstOption?.ip // 第三级IP值

      if (ip) {
        // 如果选择了IP，显示格式：安全域名称-Agent名称-IP
        return `${domainName}-${agentName}-${ip}`
      } else {
        // 如果只选择了Agent，显示格式：安全域名称-Agent名称
        return `${domainName}-${agentName}`
      }
    }

    // 如果没有找到agent信息，尝试从labels中获取信息，并用 extractLabelText 处理
    if (labels && labels.length > 0) {
      // 用 extractLabelText 提取文本
      const textLabels = labels.map(extractLabelText)
      if (textLabels.length === 3) {
        return `${textLabels[0]}-${textLabels[1]}-${textLabels[2]}`
      } else if (textLabels.length === 2) {
        return `${textLabels[0]}-${textLabels[1]}`
      } else if (textLabels.length === 1) {
        return textLabels[0]
      }
    }

    return ''
  }

  // 站点选择
  const cascaderDeviceListData = useMemo(
    () =>
      securityDeviceData.map(chain => {
        // 统计每个链路的设备总数
        const totalDevices = chain.typeDeviceList.reduce(
          (total, type) => total + type.devices.length,
          0,
        )

        // 检查当前链路是否有设备被选中
        const hasSelectedDevices = selectedDeviceIds.some(
          (devicePath: string[]) => Array.isArray(devicePath) && devicePath[0] === chain.chainId,
        )

        // 检查是否有其他链路的设备被选中
        const hasOtherChainSelected = selectedDeviceIds.some(
          (devicePath: string[]) => Array.isArray(devicePath) && devicePath[0] !== chain.chainId,
        )

        // 当前链路是否应该被禁用（有其他链路的设备被选中时）
        const isChainDisabled = hasOtherChainSelected && !hasSelectedDevices

        // 判断当前链路是否应该被禁用：chainId为"0"且没有设备类型
        const isChainOptionDisabled = chain.chainId === '0' && chain.typeDeviceList.length === 0

        return {
          value: chain.chainId, // 第一级改为chainId
          disabled: isChainOptionDisabled, // 添加禁用逻辑
          label: (
            <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>{chain.name}</span>
              {totalDevices > 0 && (
                <span style={{ color: '#4238C9', marginLeft: '8px' }}>({totalDevices})</span>
              )}
            </span>
          ),

          children: chain.typeDeviceList.map(deviceType => {
            const onlineDevices = deviceType.devices.filter(
              device => device.deviceStatus === 'DatasourceClientStatusOnline',
            )
            const offlineDevices = deviceType.devices.filter(
              device => device.deviceStatus !== 'DatasourceClientStatusOnline',
            )

            return {
              value: deviceType.deviceType,
              label: (
                <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{deviceType.deviceType}</span>
                  {deviceType.devices.length > 0 && (
                    <span style={{ color: '#4238C9', marginLeft: '8px' }}>
                      ({deviceType.devices.length})
                    </span>
                  )}
                </span>
              ),

              selectable: false, // 第二层不可选择
              children: [...onlineDevices, ...offlineDevices].map(device => ({
                value: device.deviceId,
                disabled: device.deviceStatus !== 'DatasourceClientStatusOnline' || isChainDisabled,
                label: (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span>
                      {device.deviceName}|{device.deviceIp}
                    </span>
                    <Badge
                      style={{ marginLeft: 10 }}
                      status={
                        device.deviceStatus === 'DatasourceClientStatusOnline'
                          ? 'success'
                          : 'default'
                      }
                    />
                  </span>
                ),

                device: {
                  deviceId: device.deviceId,
                  deviceName: device.deviceName,
                  deviceIp: device.deviceIp,
                  deviceStatus: device.deviceStatus,
                  chainId: device.chainId,
                },
              })),
            }
          }),
        }
      }),
    [selectedDeviceIds, securityDeviceData],
  )

  // 自定义显示渲染函数
  const displayRender = (labels: string[], selectedOptions?: DefaultOptionType[]) => {
    if (!selectedOptions) return labels
    return selectedOptions
      .map((option, index) => {
        if (option?.device) {
          return `${option.device.deviceName}|${option.device.deviceIp}`
        }
        return null
      })
      .filter(Boolean)
      .join('、')
  }

  const siteSearchRender = (inputValue: string, path: DefaultOptionType[]) => {
    if (path.length === 0) return null
    const searchValue = inputValue.toLowerCase()
    return (
      <span>
        {path.map((option, idx) => {
          const labelText = extractLabelText(option.label)
          const lowerLabel = labelText.toLowerCase()
          const matchIdx = lowerLabel.indexOf(searchValue)
          if (matchIdx !== -1 && searchValue) {
            // 高亮匹配部分
            return (
              <span key={idx}>
                {labelText.slice(0, matchIdx)}
                <span style={{ color: '#4238c9', fontWeight: 600 }}>
                  {labelText.slice(matchIdx, matchIdx + searchValue.length)}
                </span>
                {labelText.slice(matchIdx + searchValue.length)}
                {idx < path.length - 1 ? ' / ' : ''}
              </span>
            )
          }
          return (
            <span key={idx}>
              {labelText}
              {idx < path.length - 1 ? ' / ' : ''}
            </span>
          )
        })}
      </span>
    )
  }

  // 攻击主体和目标主体的处理
  useEffect(() => {
    if (initialValues.srcAgentId && initialValues.srcAgentId.length > 0) {
      createForm.setFieldsValue({
        srcAgentId: initialValues.srcAgentId,
      })
    }
    if (initialValues.dstAgentId && initialValues.dstAgentId.length > 0) {
      createForm.setFieldsValue({
        dstAgentId: initialValues.dstAgentId,
      })
    }
  }, [initialValues.srcAgentId, initialValues.dstAgentId, createForm])

  return (
    <Container>
      <Form
        id="waf-create-config-form"
        onFinish={submitForm}
        form={createForm}
        layout="horizontal"
        colon={false}
        labelWrap
        labelCol={{ flex: '75px', style: { textAlign: 'right' } }}
        onValuesChange={handleFormValueChange}
        initialValues={{
          verification_mode_id: formState.verifyMode,
          verify_mode: formState.verifyMode,
          execute_type: formState.executeType,
          execute_times: formState.executeTimes,
          execute_priority: formState.priority || 3,
          execute_template_latest_case: formState.executeTemplateLatestCase,
          reduce_speed: formState.reduceSpeed,
          send: formState.send,
          filter: formState.filter,
          emailList: formState.emailList,
          condition: formState.condition,
          task_time: formState.taskTime,
          is_nat: formState.isNat,
          pcapCollection: formState.pcapCollection,
          dstAgentId: [],
          srcAgentId: [],
          device_ids: initialValues?.device_ids || [],
        }}
      >
        {/* 任务基本信息 */}
        <div className="section">
          <div className="section-title">
            {t('任务基本信息')}
            <div className="section-bar" />
          </div>
          <div className="form-content">
            <Form.Item
              label={t('任务名称')}
              name="name"
              required
              rules={[
                { required: true, message: t('请输入任务名称') },
                { max: 50, message: t('任务名称最多50字') },
                {
                  pattern: /^(?=.*\S).+$/,
                  message: t('任务名称不能全部为空格'),
                },
              ]}
              className="form-item required"
            >
              <Input placeholder={t('请输入')} style={{ width: '300px' }} allowClear />
            </Form.Item>

            <Form.Item label={t('任务分组')} name="group_id" className="form-item">
              <Select
                placeholder={t('请选择')}
                style={{ width: 300 }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const groupName =
                    taskGroup?.find(item => item.groupId === option?.value)?.groupName || ''
                  return groupName.toLowerCase().includes(input.toLowerCase())
                }}
              >
                {taskGroup?.map(item => (
                  <Select.Option key={item.groupId} value={item.groupId}>
                    {item.groupName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        {/* 任务验证配置 */}
        <div className="section">
          <div className="section-title">
            {t('任务验证配置')}
            <div className="section-bar" />
          </div>
          <div className="form-content">
            <Form.Item
              label={t('验证模式')}
              name="verification_mode_id"
              className="form-item"
              rules={[{ required: true, message: t('请选择验证模式') }]}
              style={{ marginLeft: 35 }}
            >
              <Checkbox.Group
                disabled
                onChange={e => {
                  setFormState(prev => ({ ...prev, verifyMode: e as number[] }))
                  createForm.setFieldsValue({
                    verification_mode_id: e as number[],
                    dstAgentId: [],
                    device_ids: [],
                  })
                }}
                value={[2]}
                style={{ marginLeft: 10 }}
              >
                <Space>
                  <Checkbox value={2}>
                    Agent
                    <span style={{ color: '#A6A3D6' }}> → </span>
                    Agent
                  </Checkbox>
                </Space>
              </Checkbox.Group>
            </Form.Item>

            {formState.verifyMode.includes(2) && (
              <div className="verify-config">
                <div className="config-title-box">
                  <div className="config-title">
                    <span style={{ color: '#86909C', marginRight: 8 }}>{t('模式配置')}</span>
                    <span style={{ color: '#86909C' }}>Agent</span>
                    <span style={{ color: '#A6A3D6' }}> → </span>
                    <span style={{ color: '#86909C' }}>Agent</span>
                  </div>
                  <div className="nat-switch-box">
                    <Form.Item name="is_nat" label={t('NAT模式')}>
                      <BasSwitch
                        checked={formState.isNat}
                        onChange={value => {
                          setFormState(prev => ({ ...prev, isNat: value }))
                          // 同步更新表单字段
                          createForm.setFieldsValue({ is_nat: value })
                          // 当NAT模式关闭时，清空相关字段的值
                          if (!value) {
                            createForm.setFieldsValue({
                              sNat: '',
                              node_ip: '',
                              dNat: '',
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
                <div className="config-form">
                  <Form.Item
                    label={<span style={{ color: '#333', fontWeight: 400 }}>{t('攻击主体')}</span>}
                    required
                    className="attack-subject-form-item"
                  >
                    <div className="flex-row">
                      <div className="label">Agent</div>
                      <Form.Item
                        name="srcAgentId"
                        noStyle
                        rules={[{ required: true, message: t('请选择Agent') }]}
                      >
                        <Cascader
                          placeholder="Agent"
                          options={cascaderAgentListData}
                          showArrow
                          maxTagCount="responsive"
                          displayRender={agentDisplayRender}
                          showSearch={{
                            filter: agentFilter,
                            matchInputWidth: true,
                            render: siteSearchRender,
                            limit: 50,
                          }}
                          dropdownMenuColumnStyle={{ maxWidth: 520 }}
                          showCheckedStrategy={Cascader.SHOW_CHILD}
                          value={createForm.getFieldValue('srcAgentId') || []}
                          onChange={value => {
                            // value 可能是二维数组或三级数组
                            const arr =
                              value && value.length > 0 && Array.isArray(value[0]) ? value : [value]
                            createForm.setFieldsValue({ srcAgentId: arr })
                            // 攻击主体变更时清空目标主体和安全设备
                            createForm.setFieldsValue({ dstAgentId: [], device_ids: [] })
                            setSelectedDeviceIds([])
                            setSelectedChainId(null)
                          }}
                        />
                      </Form.Item>
                      <Button
                        type="link"
                        onClick={() => {
                          // 添加攻击机的逻辑
                          window.open('/configure/client/list', '_blank')
                        }}
                      >
                        {t('添加')}
                      </Button>
                    </div>
                  </Form.Item>

                  {formState.isNat && (
                    <>
                      <Form.Item
                        label={<span style={{ color: '#333', fontWeight: 400 }} />}
                        className="snat-form-item"
                      >
                        <div className="flex-row">
                          <div className="label">SNAT</div>
                          <Form.Item
                            name="sNat"
                            noStyle
                            rules={[
                              { max: 20, message: t('SNAT最长20个字符') },
                              {
                                pattern:
                                  /^((2((5[0-5])|([0-4]\d)))|([0-1]?\d{1,2}))(\.((2((5[0-5])|([0-4]\d)))|([0-1]?\d{1,2}))){3}$/,
                                message: t('请输入正确的IP地址格式'),
                              },
                            ]}
                          >
                            <Input placeholder={t('请输入')} style={{ width: 300 }} allowClear />
                          </Form.Item>
                        </div>
                      </Form.Item>

                      <Form.Item
                        label={
                          <span style={{ color: '#333', fontWeight: 400 }}>
                            {t('攻击过程节点')}
                          </span>
                        }
                        className="attack-node-form-item"
                      >
                        <div className="flex-row">
                          <div className="label">{t('节点IP')}</div>
                          <Form.Item
                            name="middleIps"
                            noStyle
                            rules={[
                              { max: 999, message: t('节点IP最长999个字符') },
                              {
                                pattern:
                                  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\s*,\s*(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))*$|^$/,
                                message: t('请输入正确的IP地址格式，多个IP用逗号分隔'),
                              },
                            ]}
                          >
                            <Input
                              placeholder={t('请输入IP多个用英文逗号分割')}
                              style={{ width: 300 }}
                              allowClear
                            />
                          </Form.Item>
                        </div>
                      </Form.Item>
                    </>
                  )}

                  <Form.Item
                    label={<span style={{ color: '#333', fontWeight: 400 }}>{t('目标主体')}</span>}
                    className="target-subject-form-item"
                    required
                  >
                    <div className="flex-row">
                      <div className="label">Agent</div>
                      <Form.Item
                        noStyle
                        name="dstAgentId"
                        rules={[{ required: true, message: t('请选择Agent') }]}
                      >
                        <Cascader
                          placeholder="Agent"
                          options={getFilteredTargetAgentOptions()}
                          showArrow
                          maxTagCount="responsive"
                          displayRender={agentDisplayRender}
                          showSearch={{
                            filter: agentFilter,
                            matchInputWidth: true,
                            render: siteSearchRender,
                            limit: 50,
                          }}
                          dropdownMenuColumnStyle={{ maxWidth: 520 }}
                          showCheckedStrategy={Cascader.SHOW_CHILD}
                          value={createForm.getFieldValue('dstAgentId') || []}
                          onChange={value => {
                            // value 可能是二维数组或三级数组
                            const arr =
                              value && value.length > 0 && Array.isArray(value[0]) ? value : [value]
                            createForm.setFieldsValue({ dstAgentId: arr })
                            // 目标主体变更时清空安全设备
                            createForm.setFieldsValue({ device_ids: [] })
                            setSelectedDeviceIds([])
                            setSelectedChainId(null)
                          }}
                        />
                      </Form.Item>
                      <Button
                        type="link"
                        onClick={() => {
                          window.open('/configure/client/list', '_blank')
                        }}
                      >
                        {t('添加')}
                      </Button>
                    </div>
                  </Form.Item>
                  {formState.isNat && (
                    <Form.Item
                      label={<span style={{ color: '#333', fontWeight: 400 }} />}
                      className="snat-form-item"
                    >
                      <div className="flex-row">
                        <div className="label">DNAT</div>
                        <Form.Item
                          name="dNat"
                          noStyle
                          rules={[
                            { max: 20, message: t('DNAT最长20个字符') },
                            {
                              pattern:
                                /^((2((5[0-5])|([0-4]\d)))|([0-1]?\d{1,2}))(\.((2((5[0-5])|([0-4]\d)))|([0-1]?\d{1,2}))){3}$/,
                              message: t('请输入正确的IP地址格式'),
                            },
                          ]}
                        >
                          <Input
                            placeholder={t('请输入IP地址')}
                            style={{ width: 300 }}
                            allowClear
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  )}

                  <Form.Item
                    label={<span style={{ color: '#333', fontWeight: 400 }}>{t('安全设备')}</span>}
                    className="security-device-form-item"
                  >
                    <div className="flex-row">
                      <Form.Item noStyle name="device_ids">
                        <Cascader
                          placeholder={t('安全设备')}
                          options={cascaderDeviceListData}
                          multiple={deviceMultiple}
                          showArrow
                          maxTagCount="responsive"
                          displayRender={displayRender}
                          showSearch={{
                            filter: deviceFilter,
                            matchInputWidth: true,
                            render: (inputValue, path) => {
                              if (path.length === 0) return inputValue
                              const searchValue = inputValue.toLowerCase()
                              return (
                                <span>
                                  {path.map((option, idx) => {
                                    const labelText = extractLabelText(option.label)
                                    const lowerLabel = labelText.toLowerCase()
                                    const matchIdx = lowerLabel.indexOf(searchValue)
                                    if (matchIdx !== -1 && searchValue) {
                                      return (
                                        <span key={idx}>
                                          {labelText.slice(0, matchIdx)}
                                          <span style={{ color: '#4238c9', fontWeight: 600 }}>
                                            {labelText.slice(
                                              matchIdx,
                                              matchIdx + searchValue.length,
                                            )}
                                          </span>
                                          {labelText.slice(matchIdx + searchValue.length)}
                                          {idx < path.length - 1 ? ' / ' : ''}
                                        </span>
                                      )
                                    }
                                    return (
                                      <span key={idx}>
                                        {labelText}
                                        {idx < path.length - 1 ? ' / ' : ''}
                                      </span>
                                    )
                                  })}
                                </span>
                              )
                            },
                            limit: 50,
                          }}
                          dropdownMenuColumnStyle={{ maxWidth: 520 }}
                          showCheckedStrategy={Cascader.SHOW_CHILD}
                          disabled={formState.webAppCount > 1}
                          onChange={value => {
                            console.log('Device onChange:', value)
                            console.log(
                              'Current form value:',
                              createForm.getFieldValue('device_ids'),
                            )

                            // 处理value为undefined或null的情况
                            const deviceValue = value || []

                            if (deviceValue.length === 0) {
                              setSelectedDeviceIds([])
                              setSelectedChainId(null)
                              return
                            }

                            let filteredValue = [...deviceValue]

                            // 检查链路一致性和无链路设备单选限制
                            if (deviceValue.length > 0) {
                              // 获取第一个设备的 chainId
                              const firstChainId = Array.isArray(deviceValue[0])
                                ? deviceValue[0][0]
                                : null

                              if (firstChainId === '0') {
                                // 无链路设备只能选一个，保留最后一个选择
                                filteredValue = [deviceValue[deviceValue.length - 1]]
                                console.log('无链路设备只能单选，保留最后一个:', filteredValue)
                              } else {
                                // 有链路设备，过滤出同一链路的设备
                                filteredValue = deviceValue.filter(
                                  (devicePath: string[]) =>
                                    Array.isArray(devicePath) && devicePath[0] === firstChainId,
                                )
                                console.log('过滤同一链路设备:', { firstChainId, filteredValue })
                              }

                              // 更新选中的链路ID
                              setSelectedChainId(firstChainId)
                            }

                            // 更新选择的设备状态
                            setSelectedDeviceIds(filteredValue)

                            // 如果过滤后的值与原值不同，更新表单
                            if (filteredValue.length !== deviceValue.length) {
                              createForm.setFieldsValue({ device_ids: filteredValue })
                            }

                            // 延迟检查表单值是否更新
                            setTimeout(() => {
                              console.log(
                                'After onChange, form value:',
                                createForm.getFieldValue('device_ids'),
                              )
                            }, 100)
                          }}
                        />
                      </Form.Item>
                      <Button
                        type="link"
                        onClick={() => {
                          window.open('/configure/device/access-management', '_blank')
                        }}
                      >
                        {t('添加接入设备')}
                      </Button>
                    </div>
                    {selectedChainId && selectedChainId !== '0' && (
                      <div
                        style={{
                          marginTop: 8,
                          color: '#666',
                          fontSize: '12px',
                          marginLeft: 135,
                        }}
                      >
                        {t('已选择链路设备，只能选择同一链路的其他设备')}
                      </div>
                    )}
                    {isNoChainDeviceSelected && (
                      <div
                        style={{
                          marginTop: 8,
                          color: '#ff7043',
                          fontSize: '12px',
                          marginLeft: 135,
                        }}
                      >
                        {t('当前为无链路设备，只能单选一个设备')}
                      </div>
                    )}
                  </Form.Item>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 任务执行配置 */}
        <div className="section">
          <div className="section-title">
            {t('任务执行配置')}
            <div className="section-bar" />
          </div>
          <div className="form-content">
            <Form.Item
              label={t('执行类型')}
              name="execute_type"
              required
              className="form-item radio-box"
            >
              <Radio.Group
                onChange={e => {
                  handleExecuteTypeChange(e.target.value)
                }}
                value={formState.executeType}
              >
                <Radio.Button value={1}>{t('即时任务')}</Radio.Button>
                <Radio.Button value={2}>{t('定时任务')}</Radio.Button>
                <Radio.Button value={3}>{t('周期任务')}</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <div>
              {formState.executeType === 2 && (
                <div className="execute-config">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 24,
                      marginLeft: 115,
                    }}
                  >
                    <Form.Item
                      name="task_time"
                      label={
                        <span style={{ fontWeight: 400, color: '#666' }}>{t('执行开始时间')}</span>
                      }
                      rules={[{ required: true, message: t('请选择执行开始时间') }]}
                    >
                      <DatePicker
                        placeholder={t('请选择日期与时间')}
                        style={{
                          width: '236px',
                          fontSize: '14px',
                          borderRadius: '6px',
                        }}
                        showTime
                        value={formState.taskTime}
                        onChange={value => {
                          setFormState(prev => ({ ...prev, taskTime: value }))
                        }}
                        disabledDate={disabledDate}
                        disabledTime={disabledDateTime}
                      />
                    </Form.Item>
                  </div>
                </div>
              )}
            </div>
            <div>
              {formState.executeType === 3 && (
                <div className="execute-config">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 24,
                      marginLeft: 115,
                    }}
                  >
                    <span
                      style={{ marginRight: 12, fontWeight: 400, color: '#666', marginLeft: 10 }}
                    >
                      {t('周期配置')}
                    </span>
                    <div>
                      <Input.Group compact style={{ display: 'flex' }}>
                        <Form.Item name="scheduler_type" noStyle>
                          <Select
                            style={{ width: '98px' }}
                            value={formState.schedulerType}
                            onChange={e => {
                              // 保存当前时间值
                              const currentTime = taskTimeRef.current || formState.taskTime
                              const currentMomentTime = taskTimeMomentRef.current

                              isTimeUpdating.current = true
                              setFormState(prev => ({
                                ...prev,
                                schedulerType: e,
                                taskTime: currentTime,
                              }))

                              // 确保时间值不被清空
                              if (currentMomentTime) {
                                createForm.setFieldsValue({
                                  task_time: currentMomentTime,
                                })
                              }
                              isTimeUpdating.current = false
                            }}
                          >
                            <Select.Option value="scheduler_type_daily">{t('每日')}</Select.Option>
                            <Select.Option value="scheduler_type_weekly">{t('每周')}</Select.Option>
                            <Select.Option value="scheduler_type_monthly">
                              {t('每月')}
                            </Select.Option>
                          </Select>
                        </Form.Item>
                        {formState.schedulerType === 'scheduler_type_daily' && (
                          <div
                            style={{
                              display: 'flex',
                              borderRadius: '0 6px 6px 0',
                              marginRight: 10,
                              alignItems: 'center',
                            }}
                          >
                            <Form.Item
                              name="task_time"
                              noStyle
                              rules={[{ required: true, message: t('请选择执行开始时间') }]}
                            >
                              <TimePicker
                                style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                value={formState.taskTime}
                                format="HH:mm:ss"
                                onChange={value => {
                                  if (value && value.isValid()) {
                                    isTimeUpdating.current = true
                                    taskTimeMomentRef.current = value
                                    taskTimeRef.current = value
                                    setFormState(prev => ({ ...prev, taskTime: value }))
                                    createForm.setFieldsValue({ task_time: value })
                                    isTimeUpdating.current = false
                                  } else {
                                    if (taskTimeMomentRef.current) {
                                      createForm.setFieldsValue({
                                        task_time: taskTimeMomentRef.current,
                                      })
                                    }
                                  }
                                }}
                              />
                            </Form.Item>
                          </div>
                        )}
                        {formState.schedulerType === 'scheduler_type_weekly' && (
                          <Input.Group compact>
                            <div className="date-select">
                              <Form.Item
                                name="day_of_week"
                                noStyle
                                rules={[{ required: true, message: t('请选择星期') }]}
                              >
                                <TreeSelect
                                  showSearch={false}
                                  treeData={weekList}
                                  treeCheckable
                                  maxTagCount={1}
                                  showCheckedStrategy="SHOW_PARENT"
                                  style={{
                                    width: '120px',
                                    borderRadius: 'none',
                                    textAlign: 'left',
                                  }}
                                  placeholder={t('请选择星期')}
                                  value={formState.dayOfWeek}
                                  onChange={(week: string[]) => {
                                    setFormState(prev => ({
                                      ...prev,
                                      dayOfWeek: week,
                                      taskTime: taskTimeRef.current,
                                    }))
                                  }}
                                />
                              </Form.Item>
                            </div>
                            <Form.Item
                              name="task_time"
                              noStyle
                              rules={[{ required: true, message: t('请选择执行开始时间') }]}
                            >
                              <TimePicker
                                style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                value={formState.taskTime}
                                format="HH:mm:ss"
                                onChange={value => {
                                  if (value && value.isValid()) {
                                    isTimeUpdating.current = true
                                    taskTimeMomentRef.current = value
                                    taskTimeRef.current = value
                                    setFormState(prev => ({ ...prev, taskTime: value }))
                                    createForm.setFieldsValue({ task_time: value })
                                    isTimeUpdating.current = false
                                  } else {
                                    if (taskTimeMomentRef.current) {
                                      createForm.setFieldsValue({
                                        task_time: taskTimeMomentRef.current,
                                      })
                                    }
                                  }
                                }}
                              />
                            </Form.Item>
                          </Input.Group>
                        )}
                        {formState.schedulerType === 'scheduler_type_monthly' && (
                          <Input.Group compact>
                            <div className="date-select" style={{ borderRadius: 'none' }}>
                              <Form.Item
                                name="day_of_month"
                                noStyle
                                rules={[{ required: true, message: t('请选择日期') }]}
                              >
                                <TreeSelect
                                  treeData={monthList}
                                  treeCheckable
                                  key="value"
                                  showCheckedStrategy="SHOW_PARENT"
                                  style={{ width: '120px' }}
                                  maxTagCount={1}
                                  value={formState.dayOfMonth}
                                  onChange={(e: any) => {
                                    setFormState(prev => ({
                                      ...prev,
                                      dayOfMonth: e,
                                      taskTime: taskTimeRef.current,
                                    }))
                                  }}
                                  placeholder={t('选择日期')}
                                />
                              </Form.Item>
                            </div>
                            <Form.Item
                              name="task_time"
                              noStyle
                              rules={[{ required: true, message: t('请选择执行开始时间') }]}
                            >
                              <TimePicker
                                style={{ width: '200px', borderRadius: '0 6px 6px 0' }}
                                value={formState.taskTime}
                                format="HH:mm:ss"
                                onChange={value => {
                                  if (value && value.isValid()) {
                                    isTimeUpdating.current = true
                                    taskTimeMomentRef.current = value
                                    taskTimeRef.current = value
                                    setFormState(prev => ({ ...prev, taskTime: value }))
                                    createForm.setFieldsValue({ task_time: value })
                                    isTimeUpdating.current = false
                                  } else {
                                    if (taskTimeMomentRef.current) {
                                      createForm.setFieldsValue({
                                        task_time: taskTimeMomentRef.current,
                                      })
                                    }
                                  }
                                }}
                              />
                            </Form.Item>
                          </Input.Group>
                        )}
                        <div style={{ width: 230 }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: 50, fontWeight: 400, color: '#666' }}>
                              {t('总执行')}
                            </div>
                            <Form.Item name="execution_times" noStyle>
                              <InputNumber
                                style={{ margin: '0 4px', width: '150px' }}
                                placeholder={t('默认不限次数')}
                                min={0}
                                max={999}
                                value={formState.executeTimesLimit}
                                onChange={val => {
                                  setFormState(prev => ({
                                    ...prev,
                                    executeTimesLimit: val || undefined,
                                  }))
                                }}
                              />
                            </Form.Item>
                            <span>{t('次')}</span>
                          </div>
                        </div>
                      </Input.Group>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="execute-config">
              <Form.Item
                label={t('PCAP收集')}
                valuePropName="checked"
                name="pcapCollection"
                className="form-item"
              >
                <BasSwitch
                  onChange={value => setFormState(prev => ({ ...prev, pcapCollection: value }))}
                  checked={formState.pcapCollection}
                />
              </Form.Item>
              <Form.Item
                label={
                  <div className="custom-label">
                    <span>{t('优先级')}</span>
                  </div>
                }
                name="priority"
                className="form-item"
              >
                <Select
                  placeholder={t('请选择')}
                  style={{ width: 300, marginRight: 16 }}
                  value={formState.priority}
                  onChange={value => {
                    setFormState(prev => ({ ...prev, priority: value }))
                    createForm.setFieldsValue({ priority: value })
                  }}
                >
                  <Select.Option value={1}>1</Select.Option>
                  <Select.Option value={2}>2</Select.Option>
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={4}>4</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                </Select>
                <Tooltip title={t('数字越大，优先级越高')}>
                  <SvgIcon name="提示" style={{ width: '14px', height: '14px', marginTop: 2 }} />
                </Tooltip>
              </Form.Item>
              {formState.executeType === 3 && (
                <Form.Item
                  label={t('执行增量')}
                  name="execute_template_latest_case"
                  className="execute-template-latest-case form-item"
                >
                  <Form.Item name="execute_template_latest_case" noStyle>
                    <BasSwitch
                      checked={formState.executeTemplateLatestCase}
                      onChange={handleExecuteTemplateLatestCaseChange}
                    />
                  </Form.Item>
                  <Tooltip
                    title={t('启用后则对全选用例的模版周期执行时执行新添加到模版中的用例')}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <SvgIcon
                      name="提示"
                      style={{ width: '14px', height: '14px', marginTop: 2, marginLeft: 10 }}
                    />
                  </Tooltip>
                </Form.Item>
              )}
            </div>
          </div>
        </div>

        {/* 报表发送配置 */}
        <div className="section">
          <div className="section-title">
            {t('报表发送配置')}
            <div className="section-bar" />
          </div>
          <div className="form-content">
            <div className="code" style={{ display: 'flex' }}>
              <Form.Item
                label={
                  <div className="custom-label">
                    <span>{t('发送报表')}</span>
                  </div>
                }
                valuePropName="checked"
                name="send"
                className="form-item"
              >
                <BasSwitch
                  onChange={value => setFormState(prev => ({ ...prev, send: value }))}
                  checked={formState.send}
                />
              </Form.Item>
              {formState.send && (
                <Form.Item
                  label={<span style={{ color: '#666' }}>{t('发送邮箱')}</span>}
                  name="emailList"
                  className="encoding-type-form-item"
                  rules={[{ required: true, message: t('请选择发送邮箱') }]}
                >
                  <Select
                    placeholder={t('请选择发送邮箱')}
                    mode="multiple"
                    showArrow
                    allowClear
                    maxTagCount={1}
                    style={{ width: 300 }}
                  >
                    {emailList.map(item => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.email}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>
            {formState.send && (
              <div className="code" style={{ display: 'flex' }}>
                <Form.Item
                  label={
                    <div className="custom-label">
                      <span>{t('过滤配置')}</span>
                    </div>
                  }
                  valuePropName="checked"
                  name="filter"
                  className="form-item"
                >
                  <BasSwitch
                    onChange={value => setFormState(prev => ({ ...prev, filter: value }))}
                    checked={formState.filter}
                  />
                </Form.Item>
                {formState.filter && (
                  <Form.Item
                    label={<span style={{ color: '#666' }}>{t('过滤条件')}</span>}
                    name="condition"
                    className="encoding-type-form-item"
                    rules={[{ required: true, message: t('请选择过滤条件') }]}
                  >
                    <Radio.Group>
                      <Radio value={'condition_anyway'}>{t('任意用例防护失效')}</Radio>
                      <Radio value={'condition_all_failed'}>{t('全部用例防护失效')}</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              </div>
            )}
          </div>
        </div>
      </Form>
    </Container>
  )
}

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  .section {
    margin-bottom: 24px;
    padding: 0 16px;
    .section-title {
      font-weight: 500;
      font-size: 14px;
      color: #333333;
      position: relative;
      &::after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        bottom: 0;
        width: 88px;
        height: 6px;
        background: linear-gradient(90deg, #2090a8 0%, #4238c9 100%);
        border-radius: 0px 86px 0px 100px;
        margin-top: 8px;
      }
      .section-bar {
        content: '';
        display: block;
        width: 100%;
        height: 4px;
        background: #ebeaf9;
        margin-top: 10px;
      }
    }

    .form-content {
      margin-top: 16px;
      .custom-label {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .form-item {
        margin-left: 35px;
      }
      .execute-config {
        border-radius: 8px;
        .flex-row {
          margin-left: 115px;
        }
        .execute-template-latest-case label {
          width: 70px;
          line-height: 20px;
          height: 40px;
        }
        .execute-template-latest-case {
          .ant-form-item-label {
            flex: 0 0 70px;
            white-space: normal;
            text-align: left;
            margin-right: 10px;
          }
          .ant-row {
            align-items: center;
          }
          .ant-form-item-control-input-content {
            display: flex;
            align-items: center;
            .ant-switch {
              margin-right: 5px;
            }
          }
        }
      }
      .verify-config {
        .config-title-box {
          display: flex;
          justify-content: space-between;
          align-items: center;
          .nat-switch-box {
            label {
              color: #86909c;
            }
          }
        }
        .flex-row {
          display: flex;
          align-items: center;
          gap: 0;
          width: 100%;

          .label {
            width: 80px;
            color: #666;
            margin-left: 30px;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .ant-form-item {
            flex: 1;
            margin-bottom: 0;
          }

          .ant-btn-link {
            margin-left: auto;
            flex-shrink: 0;
          }
        }
        .attack-subject-form-item,
        .snat-form-item,
        .attack-node-form-item {
          .ant-form-item-explain-error {
            margin-left: 125px;
          }
          .ant-form-item-label {
            white-space: nowrap;
            min-width: 100px;
            flex: 0 0 100px;
          }
        }
        .attack-subject-form-item {
          .ant-select-in-form-item {
            width: 300px;
          }
        }
        .security-device-form-item {
          label {
            margin-right: 12px;
          }
          .ant-select-in-form-item {
            width: 387px;
          }
          .ant-form-item-explain-error {
            margin-left: 135px;
          }
          .ant-cascader-menus .ant-cascader-menu:first-child .ant-cascader-checkbox {
            display: none !important;
          }
          .ant-cascader-menus .ant-cascader-menu:nth-child(2) .ant-cascader-checkbox {
            display: none !important;
          }
          .ant-form-item-label {
            white-space: nowrap;
            min-width: 120px;
            flex: 0 0 120px;
          }
        }
        .target-subject-form-item {
          .ant-select-in-form-item {
            width: 300px;
          }
          .ant-form-item-explain-error {
            margin-left: 125px;
          }
          .ant-form-item-label {
            white-space: nowrap;
            min-width: 100px;
            flex: 0 0 100px;
          }
        }
      }
      .radio-box {
        .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          border-color: #d9d9d9;
          color: #4238c9;
          background: #e9e8f9;
        }
        .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
          background: #d9d9d9;
        }
        .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):focus-within {
          box-shadow: none;
        }
      }
      .encoding-type-form-item {
        margin-left: 24px;
      }
    }

    .ant-form-item-label {
      font-weight: 500;
      white-space: nowrap;
      text-align: right;
    }
  }
`

const FormItem = styled(Form.Item)`
  margin-bottom: 24px;
`
